<script setup lang="ts">
import { vHasScroll } from '@dh-io-owpi/shared/src/directives/v-has-scroll';
import { type TableData } from '../utils/types';
import { computed, useId } from 'vue';
import { FootnoteReference } from '@dh-io-owpi/shared/src/components/ssr/footnotes';
import TechDataTableCell from './cell/TechDataTableCell.vue';
import ClientOnly from '@dh-io-owpi/shared/src/components/ClientOnly/ClientOnly.vue';
import { WbIcon } from '@dh-io-owpi/shared/src/components/WbIcon';
import { vWbtooltip } from '@dh-io-owpi/shared/src/directives/v-wbtooltip';

const props = defineProps<{
  data: TableData;
  /**
   * Display footnotes in a local footnote component.
   */
  footnoteModuleId?: string;
}>();

// Does this table have more than one group?
const isGrouped = computed(() => props.data.groups.length > 1);
const id = useId();
</script>
<template>
  <div class="owpi-tech-data-table" :class="{ '-is-grouped': isGrouped }" v-has-scroll>
    <table>
      <thead>
        <tr class="_head">
          <td class="_row-head"></td>
          <th scope="col" v-for="h in data.headers" :key="h" :title="h.title">
            <div class="_name wb-text-l-strong">{{ h.title }}</div>
            <div class="_price wb-text-m" v-if="h.subtitle">{{ h.subtitle }}</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(group, g) in data.groups" :key="group.label">
          <tr v-if="group.label" class="_group-head">
            <th scope="colgroup" class="wb-text-l-strong _row-head" :colspan="group.rows[0]?.values.length + 1">
              <div>{{ group.label }}</div>
            </th>
          </tr>
          <tr class="_row" v-for="(row, r) in group.rows" :key="row.id">
            <th scope="row" class="_row-head" :data-id="row.id">
              <div>
                <span class="wb-text-l-strong">{{ row.label }}</span>
                <ClientOnly v-if="row.description">
                  <wb7-tooltip v-wbtooltip role="tooltip" :aria-label="row.description">
                    <WbIcon name="bds/info-circle/24" />
                    <div slot="content">
                      {{ row.description }}
                    </div>
                  </wb7-tooltip>
                </ClientOnly>
              </div>
              <span v-if="row.unit" class="wb-text-l">({{ row.unit }})</span>
            </th>
            <td v-for="(value, i) in row.values" :key="value" class="wb-text-l">
              <TechDataTableCell :row :value />
              <FootnoteReference
                v-for="(f, j) of value.footnotes"
                :key="f"
                :content="f"
                :reference="`owpi_spa-tech-data_${id}-${g}-${r}-${i}-${j}`"
                :module-id="footnoteModuleId"
                class="_footnote"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss">
.owpi-tech-data-table {
  --_first-col-width: min(40vw, 210px);
  --_table-cell-padding-block: var(--wb-table-header-md-padding-top);
  --_table-cell-padding-inline: var(--wb-table-header-md-padding-left);

  --inline-margin: 0px;

  width: auto;
  min-height: fit-content;
  overflow-x: auto;
  overscroll-behavior-x: none;
  margin-inline-start: var(--inline-margin);

  @include wb.breakpoint-from(mq4) {
    --_table-cell-padding-block: var(--wb-table-header-lg-padding-top);
    --_table-cell-padding-inline: var(--wb-table-header-lg-padding-left);
  }

  table {
    width: calc(100% - var(--inline-margin));
    border-collapse: collapse;
    display: table;
    box-sizing: border-box;
    margin-inline-end: var(--inline-margin);
    color: var(--wb-table-header-text-color);
  }
  td {
    text-align: end;
    min-width: 150px;
  }

  th,
  td {
    padding: var(--wb-spacing-xs);
  }

  /// first column sticky styles
  ._row-head {
    min-width: var(--_first-col-width);
    max-width: var(--_first-col-width);
    background-color: inherit;
    text-align: start;
  }
  // first column empty spot needs to be on top
  ._head ._row-head {
    z-index: 2;
  }

  // first row, with name and price, sticky styles
  ._head {
    position: sticky;
    top: 0;
    background-color: var(--wb-data-table-background-main-color);
    z-index: 2;
    th {
      min-width: 100px;
      max-width: 200px;
      text-align: end;

      ._name {
        // only show 2 lines of text for motorization name and cut off the rest
        max-height: 2lh;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
  }

  // group header sticky styles
  ._group-head {
    th {
      background-color: var(--wb-table-cell-background-striped-color);
      div {
        position: sticky;
        left: var(--wb-spacing-xs);
        max-width: calc(100vw - (var(--wb-spacing-xs) * 2) - (var(--inline-margin) * 2));
      }
    }
  }

  ._row {
    background-color: var(--wb-data-table-background-main-color);

    th {
      overflow: hidden;
      > span {
        display: inline-block;
        word-wrap: none;
      }

      .wb-text-l {
        white-space: nowrap;
      }
    }
  }

  ._footnote {
    display: inline-block;
    width: 11px;
  }

  &.v-has-scroll-x {
    ._row-head {
      position: sticky;
      left: 0;
    }
    tr:not(._group-head) ._row-head::after {
      content: '';
      position: absolute;
      inset: -5px 3px -5px 0;
      box-shadow: 4px 0px 4px -4px var(--wb-table-border-color);
      opacity: 0.8;
    }
  }

  // make non-grouped rows striped
  &:not(.-is-grouped) ._row:nth-child(odd) {
    background-color: var(--wb-table-cell-background-striped-color);
  }

  // make grouped rows bordered
  &.-is-grouped {
    ._row + ._row {
      border-top: 1px solid var(--wb-table-border-color);
    }
    ._row:has(+ ._group-head) > * {
      padding-bottom: var(--wb-spacing-m);
    }
  }

  // add a a11y focus outline to the scrollable table container
  &:focus-visible {
    outline: 1px solid var(--wb-color-border-brand-active);
  }

  wb7-tooltip:not(.hydrated) {
    display: none;
  }

  wb7-tooltip {
    margin-inline-start: var(--wb-spacing-xxs);
    vertical-align: text-top;
    position: relative;
    z-index: 1;
  }

  .wb-icon {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
}
</style>
